import React, { useState, useMemo, useCallback } from "react";
import { Box, Paper, Stack } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";

import DashboardHeader from "../../Main/components/shared/DashboardHeader";
import useAuthUser from "../../../hooks/useAuthUser";
import { getDocuments, addDocument, removeDocument } from "../services/documentStorageService";
import DocumentUploadArea from "../components/DocumentUploadArea";
import DocumentsTable from "../components/DocumentsTable";
import DocumentDetailDialog from "../components/DocumentDetailDialog";

/**
 * Página principal del módulo Employee Documents.
 * Permite subir metadata de archivos y gestionar documentos.
 */
export default function EmployeeDocumentsPage() {
  const user = useAuthUser();

  // version counter — incrementar para forzar re-lectura de localStorage
  const [version, setVersion] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const documents = useMemo(() => getDocuments(), [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = useCallback(
    (filesMetadata) => {
      filesMetadata.forEach((meta) => {
        addDocument({
          ...meta,
          uploadedBy: user?.employee || user?.userId || "unknown",
          uploadedByName: user?.fullName || user?.name || "Unknown User",
        });
      });
      setVersion((v) => v + 1);
    },
    [user]
  );

  const handleDelete = useCallback((id) => {
    removeDocument(id);
    setVersion((v) => v + 1);
  }, []);

  const handleViewDetails = useCallback((doc) => {
    setSelectedDoc(doc);
    setDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setSelectedDoc(null);
  }, []);

  const handleDetailSaved = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        <DashboardHeader
          title="Employee Documents"
          subtitle="Upload and manage work-related documents — certifications, contracts, evaluations, and more."
          icon={<FolderIcon />}
          color="info"
        />

        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          <DocumentUploadArea onUpload={handleUpload} />
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          <DocumentsTable
            documents={documents}
            onViewDetails={handleViewDetails}
            onDelete={handleDelete}
          />
        </Paper>
      </Stack>

      <DocumentDetailDialog
        open={detailOpen}
        doc={selectedDoc}
        onClose={handleDetailClose}
        onSaved={handleDetailSaved}
      />
    </Box>
  );
}
